import { useEffect, useMemo, useState } from "react";
import ConfigurableValues from "../../config/constants";
import { estimateDriveMinutes } from "./pickupText";

// Driving minutes from the customer to each pickup restaurant: one Mapbox
// Directions Matrix request for the whole list (1 origin + up to 24
// destinations), as the app does. Anything it can't answer (no token, a
// failed request, more than 24 restaurants) falls back to the estimate from
// the straight-line distance, so every card still shows a drive time.

const MAX_DESTINATIONS = 24;
const cache = new Map(); // key -> { [restaurantId]: minutes }

const coordOf = (r) => {
  const c = r?.location?.coordinates;
  const lng = Number(c?.[0]);
  const lat = Number(c?.[1]);
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lng, lat] : null;
};

export default function useDriveTimes(origin, restaurants) {
  const { MAPBOX_TOKEN: token } = ConfigurableValues();
  const targets = useMemo(() => (restaurants || []).filter((r) => coordOf(r)).slice(0, MAX_DESTINATIONS), [restaurants]);
  const key = useMemo(() => {
    if (!origin?.latitude || !origin?.longitude || !targets.length) return null;
    return `${Number(origin.latitude).toFixed(4)},${Number(origin.longitude).toFixed(4)}|${targets.map((r) => r._id).join(",")}`;
  }, [origin?.latitude, origin?.longitude, targets]);
  const [measured, setMeasured] = useState(() => (key && cache.get(key)) || {});

  useEffect(() => {
    if (!key) return undefined;
    if (cache.has(key)) {
      setMeasured(cache.get(key));
      return undefined;
    }
    // A different address: don't show the last one's drive times meanwhile.
    setMeasured({});
    if (!token) return undefined;
    let cancelled = false;
    const coords = [[Number(origin.longitude), Number(origin.latitude)], ...targets.map(coordOf)];
    const path = coords.map(([lng, lat]) => `${lng.toFixed(5)},${lat.toFixed(5)}`).join(";");
    const url = `https://api.mapbox.com/directions-matrix/v1/mapbox/driving/${path}?sources=0&annotations=duration&access_token=${encodeURIComponent(token)}`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const row = json?.durations?.[0];
        if (cancelled || !Array.isArray(row)) return;
        const out = {};
        targets.forEach((r, i) => {
          const seconds = row[i + 1];
          if (Number.isFinite(seconds)) out[r._id] = Math.max(1, Math.round(seconds / 60));
        });
        cache.set(key, out);
        setMeasured(out);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [key, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (restaurant) => measured[restaurant?._id] ?? estimateDriveMinutes(restaurant?.distance);
}
